import { useId } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PencilIcon } from '../../../assets/icons/pencil.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete.svg';
import { ReactComponent as ClientIcon } from '../../../assets/icons/client.svg';
import { ReactComponent as GoalIcon } from '../../../assets/icons/goal.svg';
import { ReactComponent as PlanDetailsIcon } from '../../../assets/icons/plan-details.svg';
import { ReactComponent as RiskToleranceIcon } from '../../../assets/icons/riskToleranceRevamped.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/download.svg';
import { Icon } from '../../atoms';
import { Dropdown } from '../../atoms/Dropdown';
import { DropdownItem } from '../../atoms/DropdownItem';
import { useDropdownState } from '../../../common/hooks/useDropdownState';

type Icons = 'edit' | 'delete' | 'client' | 'goal' | 'plan-details' | 'download' | 'risk-tolerance';

export type ActionOption = {
    icon?: any;
    label: string;
    onClick(): void;
    disabled?: boolean;
};

type Props = {
    menuWidth?: number;
    options: ActionOption[];
    onOpen?(): void;
    zIndex?: number;
    showExpandButton?: boolean;
};

/**
 *
 * @param menuWidth the width size of the dropdown in pixels
 * @param options an array of ActionOption
 * @param onOpen a callback called every time the dropdown opens
 * @returns
 */
export const MoreActionsMenu = ({ menuWidth = 140, options = [], onOpen, zIndex, showExpandButton }: Props) => {
    const uniqId = useId();
    const { t } = useTranslation();

    const { toggle, isActive, ...dropdownProps } = useDropdownState({ onOpen });

    return (
        <div>
            <Container id={uniqId} showExpandButton={showExpandButton}>
                <ClickableArea type="button" aria-label={t('MORE_OPTIONS')} onClick={toggle} />
                {isActive && <IconBackground />}
                <Icon id="more-vertical" />
            </Container>

            <Dropdown {...dropdownProps} zIndex={zIndex}>
                <DropdownContent width={menuWidth} onClick={(e) => e.stopPropagation()}>
                    {options.map(({ label, onClick, icon, disabled }) => {
                        const Icon = icon && icons.get(icon);

                        return (
                            <DropdownItem
                                onClick={() => {
                                    toggle();
                                    onClick();
                                }}
                                key={`${uniqId}_${label}`}
                                disabled={disabled}
                            >
                                {icon && <Icon />}
                                <span>{label}</span>
                            </DropdownItem>
                        );
                    })}
                </DropdownContent>
            </Dropdown>
        </div>
    );
};

const Container = styled('div')<{ showExpandButton?: boolean }>(({ theme, showExpandButton }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    width: 24,
    height: 24,
    margin: '0 auto',
    '> svg': {
        zIndex: 1,
        color: showExpandButton ? theme.palette.neutral[400] : theme.palette.semantic.bg.brand,
        position: 'absolute'
    }
}));

const ClickableArea = styled('button')(() => ({
    all: 'unset',
    background: 'transparent',
    cursor: 'pointer',
    width: 30,
    height: 24,
    border: 'none',
    outline: 'none',
    position: 'absolute',
    zIndex: 2
}));

const IconBackground = styled('span')(({ theme }) => ({
    position: 'absolute',
    width: '8px',
    height: '24px',
    background: theme.palette.neutral[200],
    borderRadius: '100px',
    zIndex: 0
}));

export const DropdownContent = styled('div')<{
    maxHeight?: number;
    width?: number | string;
}>(({ maxHeight = 'unset', width, theme }) => ({
    color: theme.palette.primary[500],
    fontFeatureSettings: "'liga' off",

    /* Typescale/Normal/text-base.normal */
    fontFamily: '"TT Commons Pro"',
    fontSize:  16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 22, /* 137.5% */
    width,
    maxHeight,
    overflowY: maxHeight !== 'unset' ? 'auto' : 'visible',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    boxShadow: `2px 4px 16px 0 ${theme.palette.primary[700]}`,
    backgroundColor: theme.palette.neutral.white,
}));

// TODO: what's the correct type?
const icons = new Map<Icons, any>([
    ['edit', PencilIcon],
    ['delete', DeleteIcon],
    ['client', ClientIcon],
    ['risk-tolerance', RiskToleranceIcon],
    ['goal', GoalIcon],
    ['plan-details', PlanDetailsIcon],
    ['download', DownloadIcon]
]);
